import {LitElement, html, css} from 'lit';
import {eplibMixin} from './eplib-mixin';
import './eplib-paginate.js';
import { BLACKLAB_BASE } from './eplib-constants.js';
import EplibAnimationUtils from './eplib-animation-utils.js';
import { BootstrapCSS } from '../resources/css/bootstrap.css';
import { EplibStyles } from '../resources/css/style.css.js';

/**
 * Adapted from the pb-blacklab-results component of tei-publisher-components
 *  
 * This component talks to a remote Blacklab instance. It displays
 * a list of documents that match the query given by `pattern`.
 * 
 * For each document a list of hits (matches) is displayed each showing the hit
 * in context of the text.
 *
 * Document Id and hits are links that can be used to open the document
 * and navigate through the hits with the help of eblib-view and eblib-blacklab-highlight
 * components.
 *
 * @fires eplib-results-received
 *
 * @listens eplib-load
 * @listens eplib-initiate-search
 * @listens eplib-results-received
 *
 * @doc - an optional document Id to limit the search to
 * @param pattern - a CQL (Common Query Language) conformant query (required)
 * @perPage - a number to indicate how many result entries to display on one page. Will be passed down to eplib-paginate
 * @csspart paginator - the eplib-paginate component
 * @csspart label - the eplib-paginate label
 */
export class EplibBlacklabResults extends eplibMixin(LitElement) {

  static get properties() {
    return {
      ...super.properties,
      /**
       * results from a kwic search
       */
      data: {
        type: Object
      },
      documents:{
        type:Array
      },
      /**
       * document id
       */
      filter: {
        type: String
      },
      /**
       * how many hits per page. will be passed down to eplib-paginate
       */
      perPage: {
        type: Number,
        attribute: 'per-page'
      },
      /**
       * must be a valid CQL query as a string
       */
      pattern: {
        type: String
      },
      /**
       * first document number to be displayed
       */
      first: {
        type: Number
      },
      /**
       * sort order of query results
       */
      sort:{
        type: String
      },
      /**
       * target for links
       */
      target:{
        type: String,
        attribute: 'target'
      },
      /**
       * indicates we have requested results and are waiting on a response
       */
      loading: {
        type: Boolean
      },
      /**
       * indicates no results match query
       */
      noResults: {
        type: Boolean
      },
      /**
       * total number of hits
       */
      totalHits: {
        type: Number
      },
      /**
       * direction of navigation through results
       */
      direction: {
        type: String
      }
    };
  }

  constructor() {
    super();
    this.data = {documents: []};
    this.documents = [];
    this.first = 1;
    this.filter = null;
    this.sort = null;
    this.noResults = false;
    this.loading = false;
    this.direction = 'nav-next';
  }

  connectedCallback() {
    super.connectedCallback();

    this.subscribeTo('eplib-load', (event) => {
      // ### handle eplib-load received from eplib-paginate to set number of first displayed document
      this.first = Number(event.detail.params.start);
      this.direction = event.detail.params.direction;
      this.load();
    }, ['search']);

    window.addEventListener('eplib-initiate-search', (event) => {
      this.first = 1;
      this.pattern = event.detail.pattern;
      this.filter = event.detail.filter;
      this.sort = event.detail.sort;
      this.perPage = event.detail.perPage;
      this.load();
      this.requestUpdate();
    });

    this.subscribeTo('eplib-results-received', (event) => {
      this.data = event.detail.data;
      this.documents = this.data.documents;
      this.noResults = (this.documents.length === 0);
    }, ['search']);
  }

  render() {
    return html`
      <div class="row">
        <eplib-paginate class="${this.noResults ? 'visually-hidden' : ''}"
                        part="paginator" per-page="${this.perPage}"
                        list-description="documents"
                        total-description="${this.data.totalHits ? ' (' + this.data.totalHits.toLocaleString() + ' total hits)' : ''}"
                        range="5"
                        channel="search">
          ${this.data.totalHits} total hits
        </eplib-paginate>
        <div class="loading ${!this.loading ? ' visually-hidden' : ''}">
          <span></span>
        </div>
        <div class="d-flex text-right${this.noResults && !this.loading ? ' visually-hidden' : ''} row">
          <span class="hit-count-header">Hits</span>
        </div>
        <div class="d-flex text-center${this.noResults && !this.loading ? '' : ' visually-hidden'} row">
          <p>There are no hits matching the search criteria.</p>
        </div>
        <div class="col-md-12">
          <ul class="kwic_results">
            ${this.documents.map(document => html`
                <li>
                  <a href="${this.target}/${document.id}.xml?pattern=${encodeURIComponent(this.pattern)}&page=${document.matches[0].page[0]}"
                      target="_blank">${document.id}
                  </a>
                  <span> ${ document.year.length > 0 ? document.year + ', ' : '' } </span>
                  <span> ${ document.author.length > 0 ? document.author + ', ' : '' } </span>
                  <span> ${ document.estc.length > 0 ? document.estc + ', ' : '' } </span>
                  <i><span> ${ document.title.length > 90 ? document.title.substring(0, 90) + '...' : document.title }</span></i>
                  <span class="hit-count">${document.hits}</span>
                  <ul>
                  ${document.matches.map(match => html`
                    <li>
                      <span>
                        ${match.left}<a href="${this.target}/${document.id}.xml?pattern=${encodeURIComponent(this.pattern)}&match=${match.match.words[0]}&page=${match.page[0]}" target="_blank">${match.match.display}</a>${match.right}
                      </span>
                    </li>
                  `)}
                  </ul>
                </li>
                <br class="clear"/>
            `)}
          </ul>
        </div>
      </div>
    `;
  }

  buildDisplay(match, suppressLeadingPunct) {
    let displayText = '';
    for (var i = 0; i < match.word.length; i++) {
      match.punct[i] = match.punct[i].replace(/^\s+(?=[,\.;\)\]]{1})/, '');
      // prepend punctuation except for first word of left span
      if (i !== 0 || !suppressLeadingPunct) { displayText += match.punct[i]; }
      displayText += match.word[i];
    }
    return displayText;
  }

  transform(data) {
    var result = {
      "docs": data.summary.numberOfDocs,
      "start": +data.summary.searchParam.first + 1,
      "per-page": data.summary.searchParam.number,
      "totalHits": data.summary.numberOfHits,
      "documents": []
    };
    for (let i = 0; i < data.docs.length; i++) {
      const doc = data.docs[i];
      const authors = doc.docInfo.author ?  doc.docInfo.author.map(a => a.replace(/\.+$/, '')) : [];
      const idno = doc.docInfo.docId ? doc.docInfo.docId[0] : doc.docInfo.fromInputFile[0].replace(/.*\/|\.[^.]*$/g, '');
      const year = doc.docInfo.display_year ? doc.docInfo.display_year[0] : doc.docInfo.original_date ? doc.docInfo.original_date[0] : '????';
      let newdoc = {
        "doc": doc.docPid,
        "year": year,
        "author": authors.join('; '),
        "title": doc.docInfo.display_title[0],
        "id": idno,
        "estc": doc.docInfo.estc ? doc.docInfo.estc.join(', ') : '',
        "hits": doc.numberOfHits,
        "matches": []
      }
      for (let j = 0; j < doc.snippets.length; j++) {
        const snippet = doc.snippets[j];
        const match = {
          "match": {
              "display": this.buildDisplay(snippet.match, 0),
              "words": snippet.match.id.map(function(id){
                                              return idno.concat('-', id);
                                          })
          },
          "left": this.buildDisplay(snippet.left, 1),
          "right": this.buildDisplay(snippet.right, 0),
          "page": [ snippet.match.id[0].substr(0, snippet.match.id[0].lastIndexOf('-')) ]
        }
        newdoc.matches.push(match);
      }
      result.documents.push(newdoc);
    }
    return result;
  }

  async load() {
    if(!this.pattern) return;
    const easeOut = EplibAnimationUtils._animatePageNav(this.direction, 'out', this.shadowRoot, '.kwic_results', '.loading');
    let url = `${BLACKLAB_BASE}/docs?outputformat=json&patt=` + encodeURIComponent(this.pattern) + `&first=${this.first - 1}&number=${this.perPage}&wordsaroundhit=8`;
    if (this.filter) {
      url += `&filter=docId:${this.filter}`;
    }
    if(this.sort) {
      url += `&sort=${this.sort}`;
    }
    // For pagination to work, we cannot limit the number of hits.
    url += '&maxretrieve=-1';
    console.log(url);
    await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin'
    })
    .then((response) => response.json())
    .then((data) => {
      if (data.error) {
        alert(data.error.message);
        return false;
      }
      this.data = this.transform(data);
      localStorage.setItem('eplib-kwic-results',JSON.stringify(this.data));
      this.emitTo('eplib-results-received', {
        "count": this.data.docs ? parseInt(this.data.docs, 10) : 0,
        "start": this.data.start,
        "params": this.data.params,
        "data": this.data
      }, ['search']);
      if (easeOut) easeOut.complete();
      EplibAnimationUtils._animatePageNav(this.direction, 'in', this.shadowRoot, '.kwic_results', '.loading');
    })
    .catch((error) => {
      alert(`Error retrieving remote content: ${error}`);
      if (easeOut) easeOut.complete();
      EplibAnimationUtils._stopLoadingAnimation(this.shadowRoot, '.loading');
    });
  }

  static get styles() {
    return [
    BootstrapCSS,
    EplibStyles,
    css`
      .row {
        margin: 0.25rem;
      }
    `];
  }

}

customElements.define('eplib-blacklab-results', EplibBlacklabResults);
